import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { DocumentModel, computeBoundingBox, type DocumentSchema, type ElementSchema, type PropertyValue, type PropertySet } from '@opencad/document';
import {
  resolveFamily,
  defaultParams,
  normalizeParams,
  evalGeometry,
  boundingBoxFromGeometry,
} from '../plugins/familyRegistry';
import type { ParamValue, GraphDefinition } from '@opencad/document';
import {
  saveDocument as offlineSaveDocument,
  loadDocument as offlineLoadDocument,
  listPendingSync,
  markSynced,
} from '../lib/offlineStore';
import {
  connectToProject,
  initSyncCrdt,
  crdtFlushOfflineQueue,
  setOnDocumentSync,
  setOnRemoteDelta,
  isApplyingRemote,
  type RemoteDelta,
} from '../lib/syncAdapter';
import { isFirebaseConfigured, firebaseAuth } from '../lib/firebase';
import { type RoleId } from '../config/roles';
import { useProjectStore } from './projectStore';

/**
 * Per-project localStorage key. Previously a single 'opencad-document' key
 * was shared across every project, which made Project B show Project A's
 * elements as soon as you opened it. Namespacing by projectId keeps each
 * project's persisted state isolated.
 */
const LEGACY_DOC_KEY = 'opencad-document';
const docKey = (projectId: string): string => `opencad-document:${projectId}`;

/**
 * Write the active document to BOTH localStorage (fast path for the next
 * page load) and the offline IndexedDB store (survives quota pressure +
 * feeds the offline sync queue). Called from every mutating action so
 * edits survive refresh. Without this helper, only addElement actually
 * persisted — update / delete / layer / level / rename mutations all
 * looked saved in memory but vanished on reload.
 */
function persistDocument(doc: DocumentSchema): void {
  try {
    localStorage.setItem(docKey(doc.id), JSON.stringify(doc));
  } catch {
    // Quota exhausted or serialisation failure — non-fatal, the offline
    // store below is the authoritative persistence layer.
  }
  void offlineSaveDocument(doc.id, JSON.stringify(doc)).catch(() => {});
}

/**
 * Global write-lock. Flipped true by useDocumentReadOnlySync when the
 * user's subscription has lapsed. All element-mutating actions consult
 * this via assertWritable below and become no-ops when locked. View /
 * selection / export actions are never gated — users never lose access
 * to their own data.
 */
let _readOnly = false;
export function setDocumentReadOnly(readOnly: boolean): void {
  _readOnly = readOnly;
}
export function isDocumentReadOnly(): boolean {
  return _readOnly;
}
function assertWritable(): boolean {
  if (_readOnly) {
    // Silently no-op instead of throwing: any UI path that reaches a
    // write action despite the banner + disabled buttons is a bug we'd
    // rather log than crash on.
    // eslint-disable-next-line no-console
    console.info('[doc] write blocked (subscription read-only)');
    return false;
  }
  return true;
}

interface HistoryEntry {
  document: DocumentSchema;
  timestamp: number;
  description: string;
}

/** T-HIST-001: A record of a single document change for the history panel. */
export interface ChangeRecord {
  id: string;
  timestamp: number;
  type: 'add' | 'update' | 'delete';
  elementId: string;
  elementType: string;
  userId: string;
}

const MAX_CHANGE_HISTORY = 200;
/** Auto-create a version snapshot every N change records so the history
 *  panel accumulates real recovery points even when the user never clicks
 *  "Save Version" manually. Tuned high enough not to flood history, low
 *  enough that a working session produces several points per hour. */
const AUTO_VERSION_THRESHOLD = 25;

function maybeAutoVersion(
  model: DocumentModel | null,
  priorCount: number,
  newCount: number,
): void {
  if (!model) return;
  const prior = Math.floor(priorCount / AUTO_VERSION_THRESHOLD);
  const next  = Math.floor(newCount  / AUTO_VERSION_THRESHOLD);
  if (next > prior) {
    try { model.createVersion(`auto @ ${newCount} changes`); }
    catch { /* version write failure is non-fatal */ }
  }
}

interface DocumentState {
  document: DocumentSchema | null;
  model: DocumentModel | null;
  selectedIds: string[];
  activeTool: string;
  isOnline: boolean;
  isSaving: boolean;
  lastSaved: number | null;

  history: HistoryEntry[];
  historyIndex: number;
  canUndo: boolean;
  canRedo: boolean;

  selectedLevelId: string | null;

  userRole: RoleId | null;

  toolParams: Record<string, Record<string, unknown>>;

  /** T-HIST-001: Ordered list of recent change records (capped at MAX_CHANGE_HISTORY). */
  changeHistory: ChangeRecord[];

  /** T-REVIEW-001: Current design review status. */
  reviewStatus: 'none' | 'pending' | 'approved' | 'changes_requested';
  setReviewStatus: (status: 'none' | 'pending' | 'approved' | 'changes_requested') => void;

  initProject: (projectId: string, userId: string) => void;
  loadProject: (projectId: string, userId: string) => void;
  closeProject: () => void;
  setSelectedIds: (ids: string[]) => void;
  setActiveTool: (tool: string) => void;
  setOnlineStatus: (online: boolean) => void;

  addLayer: (params: { name: string; color: string }) => string;
  updateLayer: (layerId: string, updates: Record<string, unknown>) => void;
  deleteLayer: (layerId: string) => void;

  addElement: (params: {
    type: string;
    layerId: string;
    geometry?: { type: string; data: unknown };
    properties?: Record<string, unknown>;
  }) => string;
  updateElement: (elementId: string, updates: Record<string, unknown>) => void;
  deleteElement: (elementId: string) => void;
  setElementMaterial: (elementId: string, materialId: string) => void;

  // T-EXT-01: Parametric families
  /** Place a new instance of a registered family. Returns the element id or ''. */
  placeFamilyInstance: (familyId: string, layerId: string, params?: Record<string, ParamValue>) => string;
  /** Update one parameter of a placed family instance and recompute geometry. */
  updateFamilyParam: (elementId: string, paramId: string, value: ParamValue) => void;

  // T-EXT-02: Visual programming graphs
  /** Persist (upsert) a graph definition in the document library. */
  saveGraph: (graph: GraphDefinition) => void;
  /** Remove a graph from the document library by id. */
  deleteGraph: (graphId: string) => void;

  // T-VIS-01: Persistent element visibility — stored in the document, synced via CRDT
  hideElement: (id: string) => void;
  unhideElement: (id: string) => void;
  hideElements: (ids: string[]) => void;
  unhideAllElements: () => void;

  setToolParam: (tool: string, key: string, value: unknown) => void;

  addPset: (elementId: string, pset: { name: string; properties: Record<string, string | number | boolean> }) => void;
  updatePsetProperty: (elementId: string, psetId: string, key: string, value: unknown) => void;
  removePset: (elementId: string, psetId: string) => void;

  setUserRole: (role: RoleId | null) => void;

  undo: () => void;
  redo: () => void;
  pushHistory: (description: string) => void;

  createVersion: (message?: string) => void;
  restoreVersion: (versionNumber: number) => void;
  getVersionList: () => Array<{ version: number; timestamp: number; message?: string }>;
  loadDocumentSchema: (schema: DocumentSchema) => void;

  /** Add a section, elevation, or detail view to the document. */
  addView: (view: import('@opencad/document').ViewSchema) => string | null;
  /** T-VIZ-040 v2: Save a finished photoreal render as a 'render'-type view. */
  addRendering: (params: { name: string; png: string; width: number; height: number; samples: number; envPreset?: string }) => string | null;
  deleteRendering: (viewId: string) => void;

  setActiveLevel: (levelId: string) => void;
  addLevel: (params: { name: string; elevation: number; height?: number }) => string;
  updateLevel: (levelId: string, updates: { name?: string; elevation?: number; height?: number }) => void;
  deleteLevel: (levelId: string) => void;
  renameLevel: (levelId: string, name: string) => void;
  renameProject: (name: string) => void;
}

export const useDocumentStore = create<DocumentState>()(
  persist(
    (set, get) => ({
      document: null,
      model: null,
      selectedIds: [],
      activeTool: 'select',
      isOnline: true,
      isSaving: false,
      lastSaved: null,

      history: [],
      historyIndex: -1,
      canUndo: false,
      canRedo: false,

      selectedLevelId: null,

      userRole: null,

      reviewStatus: 'none' as const,
      setReviewStatus: (status) => set({ reviewStatus: status }),

      toolParams: {
        // thickness intentionally omitted — commit logic picks ArchiCAD-style
        // default by wallType (exterior 300 / interior 150 / partition 100 /
        // curtain 60). User can still override via the WallToolPanel.
        wall: { height: 3000, wallType: 'interior' },
        door: { height: 2100, width: 900, swing: 90 },
        window: { height: 1200, width: 1200, sillHeight: 900 },
      },

      changeHistory: [],

      initProject: (projectId, userId) => {
        let model: DocumentModel;
        const perProjectKey = docKey(projectId);
        try {
          // One-time migration: if a legacy 'opencad-document' still exists
          // AND the matching per-project key does NOT, move it into the
          // per-project slot so old projects don't lose their data. Always
          // remove the legacy key so future projects start clean.
          const legacy = localStorage.getItem(LEGACY_DOC_KEY);
          if (legacy) {
            try {
              const legacyDoc = JSON.parse(legacy);
              const legacyId = legacyDoc?.id;
              if (legacyId && !localStorage.getItem(docKey(legacyId))) {
                localStorage.setItem(docKey(legacyId), legacy);
              }
            } catch { /* ignore — legacy payload unparseable */ }
            localStorage.removeItem(LEGACY_DOC_KEY);
          }

          const saved = localStorage.getItem(perProjectKey);
          if (saved) {
            const docData = JSON.parse(saved);
            if (docData?.content && docData?.organization && docData?.id === projectId) {
              model = new DocumentModel(projectId, userId);
              try {
                model.loadDocument(docData);
              } catch (loadErr) {
                // eslint-disable-next-line no-console
                console.warn('[doc] loadDocument failed, starting fresh:', loadErr);
                localStorage.removeItem(perProjectKey);
                model = new DocumentModel(projectId, userId);
              }
            } else {
              // Schema mismatch or wrong project id — discard and start fresh.
              localStorage.removeItem(perProjectKey);
              model = new DocumentModel(projectId, userId);
            }
          } else {
            model = new DocumentModel(projectId, userId);
          }
        } catch (err) {
          // eslint-disable-next-line no-console
          console.warn('[doc] initProject parse/init failed, starting fresh:', err);
          try { localStorage.removeItem(perProjectKey); } catch { /* ignore */ }
          model = new DocumentModel(projectId, userId);
        }
        const document = model.documentData;

        // The dashboard's projectStore entry is the authoritative source for
        // the project's display name — a rename from the dashboard may have
        // happened since this doc blob was last written. Overlay that name
        // so the editor title bar and the dashboard card stay in sync.
        const projectMeta = useProjectStore.getState().projects.find((p) => p.id === projectId);
        if (projectMeta?.name && projectMeta.name !== document.name) {
          model.documentData.name = projectMeta.name;
        }

        set({
          document,
          model,
          lastSaved: Date.now(),
          history: [],
          historyIndex: -1,
          canUndo: false,
          canRedo: false,
        });

        // Initialise the CRDT WASM module (no-op if already loaded) then
        // connect to the real-time sync relay with an authenticated token.
        void initSyncCrdt().then(() => {
          connectToProject(
            projectId,
            isFirebaseConfigured
              ? () => {
                  const auth = firebaseAuth();
                  return auth.currentUser
                    ? auth.currentUser.getIdToken()
                    : Promise.resolve(null);
                }
              : undefined,
          );
        });
      },

      loadProject: (projectId, userId) => {
        const model = new DocumentModel(projectId, userId);
        const document = model.documentData;

        set({
          document,
          model,
          lastSaved: Date.now(),
          history: [],
          historyIndex: -1,
          canUndo: false,
          canRedo: false,
        });

        // Initialise the CRDT WASM module (no-op if already loaded) then
        // connect to the real-time sync relay with an authenticated token.
        void initSyncCrdt().then(() => {
          connectToProject(
            projectId,
            isFirebaseConfigured
              ? () => {
                  const auth = firebaseAuth();
                  return auth.currentUser
                    ? auth.currentUser.getIdToken()
                    : Promise.resolve(null);
                }
              : undefined,
          );
        });
      },

      /** Clear the active document so the ProjectHomeScreen is shown. */
      closeProject: () => {
        set({
          document: null,
          model: null,
          selectedIds: [],
          activeTool: 'select',
          history: [],
          historyIndex: -1,
          canUndo: false,
          canRedo: false,
          lastSaved: null,
        });
      },

      setSelectedIds: (ids) => set({ selectedIds: ids }),

      setActiveTool: (tool) => set({ activeTool: tool }),

      setOnlineStatus: (online) => {
        const { model, isOnline } = get();
        if (model) {
          model.setOnlineStatus(online);
        }
        set({ isOnline: online });

        // When transitioning from offline → online, flush any pending offline edits.
        if (online && !isOnline) {
          crdtFlushOfflineQueue();
          void listPendingSync().then((pendingIds) => {
            for (const pid of pendingIds) {
              void offlineLoadDocument(pid).then((data) => {
                if (!data) return;
                // Re-save locally to mark synced (no server API on this branch)
                void markSynced(pid).catch(() => {});
              }).catch(() => {});
            }
          }).catch(() => {});
        }
      },

      addLayer: (params) => {
        const { model } = get();
        if (!model) throw new Error('No document loaded');

        const layerId = model.addLayer(params);
        const doc = { ...model.documentData };
        set({ document: doc, lastSaved: Date.now() });
        persistDocument(doc);
        return layerId;
      },

      updateLayer: (layerId, updates) => {
        const { model } = get();
        if (!model) return;

        model.updateLayer(layerId, updates as Record<string, unknown>);
        const doc = { ...model.documentData };
        set({ document: doc, lastSaved: Date.now() });
        persistDocument(doc);
      },

      deleteLayer: (layerId) => {
        const { model } = get();
        if (!model) return;

        model.deleteLayer(layerId);
        const doc = { ...model.documentData };
        set({ document: doc, lastSaved: Date.now() });
        persistDocument(doc);
      },

      addElement: (params) => {
        if (!assertWritable()) return '';
        const { model, changeHistory } = get();
        if (!model) throw new Error('No document loaded');

        const props = (params.properties || {}) as Record<string, PropertyValue>;
        const elementId = model.addElement({
          type: params.type as 'wall' | 'door' | 'window' | 'slab',
          layerId: params.layerId,
          geometry: params.geometry as import('@opencad/document').ElementGeometry | undefined,
          properties: props,
        });

        // Compute bounding box from element properties
        const createdEl = model.documentData.content.elements[elementId];
        if (createdEl) {
          createdEl.boundingBox = computeBoundingBox(params.type, props);
          // Apply optional geometry override (e.g. text tool, spline curves)
          if (params.geometry) {
            createdEl.geometry = params.geometry as typeof createdEl.geometry;
          }
        }

        const newDoc = { ...model.documentData };
        const addRecord: ChangeRecord = {
          id: crypto.randomUUID(),
          timestamp: Date.now(),
          type: 'add',
          elementId,
          elementType: params.type,
          userId: model.client,
        };
        set({
          document: newDoc,
          lastSaved: Date.now(),
          changeHistory: [...changeHistory, addRecord].slice(-MAX_CHANGE_HISTORY),
        });
        maybeAutoVersion(model, changeHistory.length, changeHistory.length + 1);
        persistDocument(newDoc);
        return elementId;
      },

      updateElement: (elementId, updates) => {
        if (!assertWritable()) return;
        const { model, changeHistory } = get();
        if (!model) return;

        const element = model.getElementById(elementId);
        if (element) {
          const updateRecord: ChangeRecord = {
            id: crypto.randomUUID(),
            timestamp: Date.now(),
            type: 'update',
            elementId,
            elementType: element.type ?? 'unknown',
            userId: model.client,
          };
          // Replace the element reference so consumers that dirty-check by
          // identity (e.g. the 3D viewport's prev !== element test) rebuild.
          // Mutating in place silently skipped viewport updates.
          const nextElement = { ...element, ...updates };
          model.documentData.content.elements[elementId] = nextElement;
          const doc = { ...model.documentData };
          set({
            document: doc,
            lastSaved: Date.now(),
            changeHistory: [...changeHistory, updateRecord].slice(-MAX_CHANGE_HISTORY),
          });
          maybeAutoVersion(model, changeHistory.length, changeHistory.length + 1);
          persistDocument(doc);
        }
      },

      deleteElement: (elementId) => {
        if (!assertWritable()) return;
        const { model, document, changeHistory } = get();
        if (!model || !document) return;

        const deletedEl = document.content.elements[elementId];
        const deleteRecord: ChangeRecord = {
          id: crypto.randomUUID(),
          timestamp: Date.now(),
          type: 'delete',
          elementId,
          elementType: deletedEl?.type ?? 'unknown',
          userId: model.client,
        };
        delete document.content.elements[elementId];
        const doc = { ...document };
        set({
          document: doc,
          lastSaved: Date.now(),
          changeHistory: [...changeHistory, deleteRecord].slice(-MAX_CHANGE_HISTORY),
        });
        maybeAutoVersion(model, changeHistory.length, changeHistory.length + 1);
        persistDocument(doc);
      },

      setElementMaterial: (elementId, materialId) => {
        if (!assertWritable()) return;
        const { model } = get();
        if (!model) return;
        const element = model.getElementById(elementId);
        if (!element) return;
        // Canonical key is 'Material' — matches every placement tool, the
        // schedules module, quantityTakeoff, and the IFC/DXF serializers.
        // Drop any legacy 'MaterialId' so the element doesn't carry both.
        const { MaterialId: _legacy, ...restProps } = element.properties ?? {};
        void _legacy;
        const nextProperties = {
          ...restProps,
          Material: { type: 'string' as const, value: materialId },
        };
        // Replace the element reference so the 3D viewport's identity-based
        // dirty check (prev !== element) fires and rebuilds the mesh with
        // textured materials. Mutating in place silently skipped rebuild.
        const nextElement = { ...element, properties: nextProperties };
        model.documentData.content.elements[elementId] = nextElement;
        const doc = { ...model.documentData };
        set({ document: doc, lastSaved: Date.now() });
        persistDocument(doc);
      },

      // T-EXT-01: Parametric family placement + param editing ────────────────

      placeFamilyInstance: (familyId, layerId, overrideParams) => {
        if (!assertWritable()) return '';
        const def = resolveFamily(familyId);
        if (!def) {
          console.error(`[family] Unknown family: ${familyId}`);
          return '';
        }
        const params = normalizeParams(def, { ...defaultParams(def), ...(overrideParams ?? {}) });
        const geom = evalGeometry(def, params);

        // Build element properties from current params
        const properties: Record<string, PropertyValue> = {};
        for (const schema of def.parameters) {
          const v = params[schema.id];
          properties[schema.id] = {
            type: schema.type === 'dimension' ? 'number' : schema.type === 'material-ref' ? 'reference' : schema.type as PropertyValue['type'],
            value: v,
            unit: schema.unit,
          };
        }

        // Build bounding box from geometry
        const bb = geom ? boundingBoxFromGeometry(geom) : { minX: -0.5, minY: -0.5, minZ: 0, maxX: 0.5, maxY: 0.5, maxZ: 1 };
        const elementGeom = geom
          ? { type: 'mesh' as const, data: { vertices: geom.vertices, faces: geom.faces } }
          : { type: 'mesh' as const, data: {} };

        const { model, changeHistory } = get();
        if (!model) return '';

        const elementId = model.addElement({
          type: def.category,
          layerId,
          geometry: elementGeom,
          properties,
        });

        const el = model.documentData.content.elements[elementId];
        if (el) {
          el.family = { familyId: def.id, version: def.version, params };
          el.boundingBox = {
            min: { _type: 'Point3D', x: bb.minX, y: bb.minY, z: bb.minZ },
            max: { _type: 'Point3D', x: bb.maxX, y: bb.maxY, z: bb.maxZ },
          };
          if (geom) el.geometry = elementGeom;
        }

        const newDoc = { ...model.documentData };
        const record: ChangeRecord = {
          id: crypto.randomUUID(),
          timestamp: Date.now(),
          type: 'add',
          elementId,
          elementType: def.category,
          userId: model.client,
        };
        set({
          document: newDoc,
          lastSaved: Date.now(),
          changeHistory: [...changeHistory, record].slice(-MAX_CHANGE_HISTORY),
        });
        maybeAutoVersion(model, changeHistory.length, changeHistory.length + 1);
        persistDocument(newDoc);
        return elementId;
      },

      updateFamilyParam: (elementId, paramId, value) => {
        if (!assertWritable()) return;
        const { model, changeHistory } = get();
        if (!model) return;

        const el = model.getElementById(elementId);
        if (!el?.family) return;

        const def = resolveFamily(el.family.familyId);
        if (!def) return;

        const nextParams = normalizeParams(def, { ...el.family.params, [paramId]: value });
        const geom = evalGeometry(def, nextParams);

        const nextProperties: Record<string, PropertyValue> = { ...el.properties };
        for (const schema of def.parameters) {
          nextProperties[schema.id] = {
            type: schema.type === 'dimension' ? 'number' : schema.type === 'material-ref' ? 'reference' : schema.type as PropertyValue['type'],
            value: nextParams[schema.id],
            unit: schema.unit,
          };
        }

        const bb = geom ? boundingBoxFromGeometry(geom) : undefined;
        const nextElement: ElementSchema = {
          ...el,
          family: { ...el.family, params: nextParams },
          properties: nextProperties,
          ...(geom ? { geometry: { type: 'mesh', data: { vertices: geom.vertices, faces: geom.faces } } } : {}),
          ...(bb ? { boundingBox: {
            min: { _type: 'Point3D', x: bb.minX, y: bb.minY, z: bb.minZ },
            max: { _type: 'Point3D', x: bb.maxX, y: bb.maxY, z: bb.maxZ },
          } } : {}),
        };

        model.documentData.content.elements[elementId] = nextElement;
        const doc = { ...model.documentData };
        const record: ChangeRecord = {
          id: crypto.randomUUID(),
          timestamp: Date.now(),
          type: 'update',
          elementId,
          elementType: el.type ?? 'unknown',
          userId: model.client,
        };
        set({
          document: doc,
          lastSaved: Date.now(),
          changeHistory: [...changeHistory, record].slice(-MAX_CHANGE_HISTORY),
        });
        maybeAutoVersion(model, changeHistory.length, changeHistory.length + 1);
        persistDocument(doc);
      },

      // T-EXT-02: Visual programming graphs ─────────────────────────────────

      saveGraph: (graph) => {
        const { model } = get();
        if (!model) return;
        const lib = model.documentData.library;
        const existing = lib.graphs ?? [];
        const idx = existing.findIndex((g) => g.id === graph.id);
        const next = idx >= 0
          ? existing.map((g, i) => (i === idx ? graph : g))
          : [...existing, graph];
        model.documentData.library = { ...lib, graphs: next };
        const doc = { ...model.documentData };
        set({ document: doc, lastSaved: Date.now() });
        persistDocument(doc);
      },

      deleteGraph: (graphId) => {
        const { model } = get();
        if (!model) return;
        const lib = model.documentData.library;
        model.documentData.library = { ...lib, graphs: (lib.graphs ?? []).filter((g) => g.id !== graphId) };
        const doc = { ...model.documentData };
        set({ document: doc, lastSaved: Date.now() });
        persistDocument(doc);
      },

      // T-VIS-01: Persistent element visibility ──────────────────────────────

      hideElement: (id) => {
        if (!assertWritable()) return;
        const { model } = get();
        if (!model) return;
        const el = model.documentData.content.elements[id];
        if (!el || el.visible === false) return;
        model.documentData.content.elements[id] = { ...el, visible: false };
        const doc = { ...model.documentData };
        set({ document: doc, lastSaved: Date.now(), selectedIds: get().selectedIds.filter((s) => s !== id) });
        persistDocument(doc);
      },

      unhideElement: (id) => {
        if (!assertWritable()) return;
        const { model } = get();
        if (!model) return;
        const el = model.documentData.content.elements[id];
        if (!el || el.visible !== false) return;
        model.documentData.content.elements[id] = { ...el, visible: true };
        const doc = { ...model.documentData };
        set({ document: doc, lastSaved: Date.now() });
        persistDocument(doc);
      },

      hideElements: (ids) => {
        if (!assertWritable()) return;
        const { model } = get();
        if (!model) return;
        if (ids.length === 0) return;
        for (const id of ids) {
          const el = model.documentData.content.elements[id];
          if (el && el.visible !== false) {
            model.documentData.content.elements[id] = { ...el, visible: false };
          }
        }
        const hiddenSet = new Set(ids);
        const doc = { ...model.documentData };
        set({ document: doc, lastSaved: Date.now(), selectedIds: get().selectedIds.filter((s) => !hiddenSet.has(s)) });
        persistDocument(doc);
      },

      unhideAllElements: () => {
        if (!assertWritable()) return;
        const { model } = get();
        if (!model) return;
        const elements = model.documentData.content.elements;
        let changed = false;
        for (const [id, el] of Object.entries(elements)) {
          if (el.visible === false) {
            elements[id] = { ...el, visible: true };
            changed = true;
          }
        }
        if (!changed) return;
        const doc = { ...model.documentData };
        set({ document: doc, lastSaved: Date.now() });
        persistDocument(doc);
      },

      setToolParam: (tool, key, value) => {
        const { toolParams } = get();
        set({ toolParams: { ...toolParams, [tool]: { ...(toolParams[tool] ?? {}), [key]: value } } });
      },

      addPset: (elementId, pset) => {
        const { model } = get();
        if (!model) return;
        const element = model.getElementById(elementId);
        if (!element) return;
        const newPset: PropertySet = {
          id: crypto.randomUUID(),
          name: pset.name,
          properties: Object.fromEntries(
            Object.entries(pset.properties).map(([k, v]) => [
              k,
              {
                type: (typeof v === 'boolean' ? 'boolean' : typeof v === 'number' ? 'number' : 'string') as 'boolean' | 'number' | 'string',
                value: v,
              },
            ])
          ),
        };
        element.propertySets = [...(element.propertySets ?? []), newPset];
        const doc = { ...model.documentData };
        set({ document: doc, lastSaved: Date.now() });
        persistDocument(doc);
      },

      updatePsetProperty: (elementId, psetId, key, value) => {
        const { model } = get();
        if (!model) return;
        const element = model.getElementById(elementId);
        if (!element) return;
        const typedValue = value as string | number | boolean | string[];
        element.propertySets = (element.propertySets ?? []).map((pset: PropertySet) => {
          if (pset.id !== psetId) return pset;
          const existing = pset.properties[key];
          const type = existing?.type ?? (typeof value === 'boolean' ? 'boolean' : typeof value === 'number' ? 'number' : 'string');
          return {
            ...pset,
            properties: {
              ...pset.properties,
              [key]: { type, value: typedValue },
            },
          };
        });
        const doc = { ...model.documentData };
        set({ document: doc, lastSaved: Date.now() });
        persistDocument(doc);
      },

      removePset: (elementId, psetId) => {
        const { model } = get();
        if (!model) return;
        const element = model.getElementById(elementId);
        if (!element) return;
        element.propertySets = (element.propertySets ?? []).filter((pset: PropertySet) => pset.id !== psetId);
        const doc = { ...model.documentData };
        set({ document: doc, lastSaved: Date.now() });
        persistDocument(doc);
      },

      setUserRole: (role) => set({ userRole: role }),

      /**
       * Add a section, elevation, or detail view to the document.
       * Returns the new view id.
       */
      addView: (view: import('@opencad/document').ViewSchema): string | null => {
        if (!assertWritable()) return null;
        const { model } = get();
        if (!model) return null;
        model.documentData.presentation.views[view.id] = view;
        const newDoc = { ...model.documentData };
        set({ document: newDoc, lastSaved: Date.now() });
        persistDocument(newDoc);
        return view.id;
      },

      /**
       * Save a photoreal render as a `'render'` view in the document.
       * The PNG is stored as a data URI alongside the view entry so the
       * rendering survives refresh without external blob storage.
       * Returns the new view id so the caller can surface / select it.
       */
      addRendering: (params: { name: string; png: string; width: number; height: number; samples: number; envPreset?: string }): string | null => {
        if (!assertWritable()) return null;
        const { model } = get();
        if (!model) return null;
        const id = `render_${Date.now().toString(36)}_${Math.floor(Math.random() * 0x10000).toString(16)}`;
        model.documentData.presentation.views[id] = {
          id,
          name: params.name,
          type: 'render',
          camera: { position: { x: 0, y: 0, z: 0 }, target: { x: 0, y: 0, z: 0 }, up: { x: 0, y: 1, z: 0 }, fov: 50, near: 0.1, far: 10_000 },
          render: {
            png: params.png,
            width: params.width,
            height: params.height,
            samples: params.samples,
            envPreset: params.envPreset,
            createdAt: Date.now(),
          },
        };
        const newDoc = { ...model.documentData };
        set({ document: newDoc, lastSaved: Date.now() });
        persistDocument(newDoc);
        return id;
      },

      deleteRendering: (viewId: string): void => {
        if (!assertWritable()) return;
        const { model } = get();
        if (!model) return;
        const view = model.documentData.presentation.views[viewId];
        if (!view || view.type !== 'render') return;
        delete model.documentData.presentation.views[viewId];
        const newDoc = { ...model.documentData };
        set({ document: newDoc, lastSaved: Date.now() });
        persistDocument(newDoc);
      },

      pushHistory: (description) => {
        const MAX_HISTORY = 50;
        const { document, history, historyIndex } = get();
        if (!document) return;

        let newHistory = history.slice(0, historyIndex + 1);
        newHistory.push({
          document: JSON.parse(JSON.stringify(document)),
          timestamp: Date.now(),
          description,
        });

        if (newHistory.length > MAX_HISTORY) {
          newHistory = newHistory.slice(newHistory.length - MAX_HISTORY);
        }

        set({
          history: newHistory,
          historyIndex: newHistory.length - 1,
          canUndo: newHistory.length > 1,
          canRedo: false,
        });
      },

      undo: () => {
        const { history, historyIndex } = get();
        if (historyIndex <= 0) return;

        const newIndex = historyIndex - 1;
        const doc = JSON.parse(JSON.stringify(history[newIndex].document)) as DocumentSchema;
        set({
          document: doc,
          historyIndex: newIndex,
          canUndo: newIndex > 0,
          canRedo: true,
          lastSaved: Date.now(),
        });
        persistDocument(doc);
      },

      redo: () => {
        const { history, historyIndex } = get();
        if (historyIndex >= history.length - 1) return;

        const newIndex = historyIndex + 1;
        const doc = JSON.parse(JSON.stringify(history[newIndex].document)) as DocumentSchema;
        set({
          document: doc,
          historyIndex: newIndex,
          canUndo: true,
          canRedo: newIndex < history.length - 1,
          lastSaved: Date.now(),
        });
        persistDocument(doc);
      },

      createVersion: (message) => {
        const { model } = get();
        if (!model) return;

        model.createVersion(message);
        const doc = { ...model.documentData };
        set({ document: doc, lastSaved: Date.now() });
        persistDocument(doc);
      },

      restoreVersion: (versionNumber) => {
        const { model } = get();
        if (!model) return;

        model.restoreVersion(versionNumber);
        const doc = { ...model.documentData };
        set({ document: doc, lastSaved: Date.now() });
        persistDocument(doc);
      },

      getVersionList: () => {
        const { model } = get();
        if (!model) return [];
        return model.getVersionList();
      },

      loadDocumentSchema: (schema) => {
        const existing = get().model;
        const userId = existing ? existing.documentData.metadata.createdBy : 'user-1';
        const newModel = new DocumentModel(schema.id, userId);
        newModel.loadDocument(schema);
        const doc = { ...newModel.documentData };
        set({
          document: doc,
          model: newModel,
          lastSaved: Date.now(),
          history: [],
          historyIndex: -1,
          canUndo: false,
          canRedo: false,
        });
        persistDocument(doc);
      },

      setActiveLevel: (levelId) => {
        set({ selectedLevelId: levelId });
      },

      addLevel: (params) => {
        const { model } = get();
        if (!model) throw new Error('No document loaded');

        const levelId = model.addLevel({ name: params.name, elevation: params.elevation, height: params.height });
        const doc = { ...model.documentData };
        set({
          document: doc,
          selectedLevelId: levelId,
          lastSaved: Date.now(),
        });
        persistDocument(doc);
        return levelId;
      },

      updateLevel: (levelId, updates) => {
        const { document } = get();
        if (!document) throw new Error('No document loaded');
        const level = document.organization.levels[levelId];
        // Match updateLayer's throw-on-missing contract (audit 2026-04-19).
        if (!level) throw new Error(`Level not found: ${levelId}`);
        Object.assign(level, updates);
        const doc = {
          ...document,
          organization: {
            ...document.organization,
            levels: { ...document.organization.levels, [levelId]: { ...level } },
          },
        };
        set({ document: doc, lastSaved: Date.now() });
        persistDocument(doc);
      },

      deleteLevel: (levelId) => {
        const { model } = get();
        if (!model) throw new Error('No document loaded');

        const levels = model.documentData.organization.levels;
        if (!levels[levelId]) throw new Error(`Level not found: ${levelId}`);
        if (Object.keys(levels).length <= 1) {
          throw new Error('Cannot delete the last level');
        }

        delete levels[levelId];
        const remainingIds = Object.keys(levels);
        const doc = { ...model.documentData };
        set({
          document: doc,
          selectedLevelId: remainingIds[0] ?? null,
          lastSaved: Date.now(),
        });
        persistDocument(doc);
      },

      renameLevel: (levelId, name) => {
        const { model } = get();
        if (!model) return;

        const level = model.documentData.organization.levels[levelId];
        if (!level) return;
        level.name = name;
        model.documentData.metadata.updatedAt = Date.now();
        const doc = { ...model.documentData };
        set({ document: doc, lastSaved: Date.now() });
        persistDocument(doc);
      },

      renameProject: (name) => {
        const { model } = get();
        if (!model) return;
        model.documentData.name = name;
        model.documentData.metadata.updatedAt = Date.now();
        const newDoc = { ...model.documentData };
        set({ document: newDoc, lastSaved: Date.now() });
        persistDocument(newDoc);
      },
    }),
    {
      name: 'opencad-ui',
      partialize: (state) => ({ activeTool: state.activeTool }),
    }
  )
);

// ── Module-level CRDT callback registration ───────────────────────────────────
// Registered once at module load so that sync events from the WebSocket relay
// are applied to the store regardless of which component is mounted.

setOnDocumentSync((data: string) => {
  try {
    const schema = JSON.parse(data) as DocumentSchema;
    const { document, loadDocumentSchema } = useDocumentStore.getState();
    const hasLocalContent =
      document ? Object.keys(document.content?.elements ?? {}).length > 0 : false;
    if (!hasLocalContent) {
      loadDocumentSchema(schema);
    }
  } catch { /* ignore malformed sync data */ }
});

setOnRemoteDelta((delta: RemoteDelta) => {
  if (isApplyingRemote()) return;
  const { document } = useDocumentStore.getState();
  if (!document) return;

  switch (delta.op) {
    case 'set': {
      document.content.elements[delta.elementId] = {
        ...(document.content.elements[delta.elementId] ?? {}),
        ...(delta.value as Partial<ElementSchema>),
      } as ElementSchema;
      useDocumentStore.setState({ document: { ...document } });
      break;
    }
    case 'setprop': {
      const el = document.content.elements[delta.elementId];
      if (!el) return;
      (el as unknown as Record<string, unknown>)[delta.prop] = delta.value;
      useDocumentStore.setState({ document: { ...document } });
      break;
    }
    case 'delete': {
      delete document.content.elements[delta.elementId];
      useDocumentStore.setState({ document: { ...document } });
      break;
    }
  }
});
